/* Play/pause and load policy for shared media slots.
 *
 * One delegated listener for the whole page rather than a script per slot: these films are
 * rendered by a snippet that can appear many times on one PDP, and a per-slot inline script
 * would run the same code a dozen times and re-register on every theme-editor repaint.
 *
 * Five things this has to get right:
 *   - reduced motion means the film must not start on its own, whatever the setting says
 *   - a film off screen should not burn battery, so it pauses when scrolled away, but only
 *     if the reader has not deliberately paused or played it
 *   - aria-pressed is the source of truth for the button's glyph, so it is always written
 *   - a section that draws a desktop and a mobile arrangement renders the film twice and
 *     hides one in CSS. Both used to download. The hidden one now gets no source at all
 *     until its breakpoint matches, which was 25MB of the 108MB one PDP was shipping.
 *   - a dozen films all calling play() on one connection starve each other into a visible
 *     stutter, so entering and leaving are held apart and buffering starts before arrival
 *
 * Scope: elements this snippet rendered, plus any element carrying data-bn-mq. Ten other
 * controllers on this store drive their own films with their own scrubbing and stage logic;
 * claiming those here would mean two scripts fighting over one element.
 */
(function () {
  // Several independently addable sections can request this shared asset on one page.
  // Register the delegated controller only once even when the browser executes it again.
  if (window.__bnMediaControllerInitialized) return;
  window.__bnMediaControllerInitialized = true;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  // Play once a quarter of the film is showing, but do not let go until almost none of it
  // is. Without that gap a film sitting near a threshold flickers between play and pause on
  // every scroll tick, which is the stutter the reader actually sees.
  var PLAY_AT = 0.25;
  var DROP_AT = 0.05;
  // A film taller than the viewport can never reach a high ratio of ITSELF, so it also
  // counts as showing when it covers half the screen.
  var COVER_AT = 0.5;
  // Start buffering this far before the film arrives, so it has something to play from by
  // the time it does.
  var PREROLL = '400px';

  function reduced() {
    return !!(REDUCE && REDUCE.matches);
  }

  function label(btn, playing) {
    if (!btn) return;
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    var t = btn.querySelector('[data-bn-media-btn-text]');
    if (t) t.textContent = playing ? window.BionoboI18n.s_08e020a353136c7df0b8b0e8 : window.BionoboI18n.s_4e2641089e8546d31f0d0f80;
  }

  function videoFor(btn) {
    var id = btn.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function btnFor(v) {
    var wrap = v.closest ? v.closest('[data-bn-media]') : null;
    return wrap ? wrap.querySelector('.bn-media__btn') : null;
  }

  function held(v) {
    var btn = btnFor(v);
    return !!(btn && btn.dataset.bnMediaHeld);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.bn-media__btn') : null;
    if (!btn) return;
    var v = videoFor(btn);
    if (!v) return;
    // Mark it as the reader's decision so the scroll observer stops overriding it.
    btn.dataset.bnMediaHeld = '1';
    if (v.paused) {
      warm(v);
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
      label(btn, true);
    } else {
      v.pause();
      label(btn, false);
    }
  });

  /* ---- the HLS safety net ------------------------------------------------- */

  // Films list the HLS playlist before the MP4, because it is the only way to serve anything
  // but the full-size file. Source selection reads canPlayType, and for HLS every browser
  // tested answers "maybe": Chrome 151 and Safari both then play it, which is the point.
  //
  // "maybe" is a guess though, and a browser that guesses wrong does NOT fall through to the
  // next <source>. Selection happens once; a decode failure afterwards fires error and the
  // element stops, showing its poster forever. So this catches that error, drops the playlist
  // and reloads onto the MP4 underneath.
  //
  // Document level and capture phase, because media error events do not bubble, and because
  // this has to cover the ten sections that write their own <video> as well as the ones this
  // snippet renders. It only ever acts on a failure, so it cannot fight another controller.
  function dropHls(v) {
    if (v.dataset.bnHlsDropped) return;
    var hls = v.querySelector('source[type="application/x-mpegURL"]');
    if (!hls) return;
    v.dataset.bnHlsDropped = '1';
    var t = v.currentTime || 0;
    hls.parentNode.removeChild(hls);
    v.load();
    var resume = function () {
      try {
        if (t > 0 && t < (v.duration || 0)) v.currentTime = t;
      } catch (e) {
        /* seeking before metadata is not worth a broken film */
      }
      if (!reduced() && !held(v)) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
    };
    v.addEventListener('loadedmetadata', resume, { once: true });
  }

  document.addEventListener(
    'error',
    function (e) {
      var t = e.target;
      if (!t) return;
      if (t.tagName === 'SOURCE' && t.parentNode && t.parentNode.tagName === 'VIDEO') {
        if ((t.getAttribute('type') || '').indexOf('mpegURL') !== -1) dropHls(t.parentNode);
        return;
      }
      if (t.tagName === 'VIDEO' && /\.m3u8/i.test(t.currentSrc || '')) dropHls(t);
    },
    true
  );

  /* ---- one retry for a refused autoplay ----------------------------------- */

  // A film that asks to play before it can decode anything gets refused, and most of the
  // hand-written players on this store call play() once and swallow the rejection. With a
  // progressive MP4 that was survivable, because the first bytes arrive quickly. With an HLS
  // playlist there is a manifest to fetch first, so the refusal became the normal case and
  // the difference band sat on its poster.
  //
  // Document level and capture phase, since canplay does not bubble, and gated on the
  // autoplay attribute so it only ever touches a film that was ASKING to play. The scrubbed
  // ones drop that attribute precisely because they are driven by scroll, so they are
  // untouched. Once per element.
  document.addEventListener(
    'canplay',
    function (e) {
      var v = e.target;
      if (!v || v.tagName !== 'VIDEO') return;
      if (!v.hasAttribute('autoplay')) return;
      if (!v.paused || v.currentTime > 0) return;
      if (v.dataset.bnAutoRetried || reduced() || held(v)) return;
      var r = v.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!r.width || r.bottom < 0 || r.top > vh) return;
      v.dataset.bnAutoRetried = '1';
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    },
    true
  );

  /* ---- breakpoint gating -------------------------------------------------- */

  // A source withheld behind data-bn-src costs nothing until its query matches. Assigning
  // src is not enough on its own: the element has already decided it has no source, so it
  // needs load() before it will fetch.
  function applyQuery(v) {
    var q = v.getAttribute('data-bn-mq');
    if (!q) return true;
    // Two sources, HLS then MP4, so this has to walk the list rather than take the first.
    var srcs = v.querySelectorAll('source[data-bn-src]');
    if (!srcs.length) return true;
    var matches = window.matchMedia(q).matches;
    var on = srcs[0].getAttribute('src');
    var i;
    if (matches && !on) {
      for (i = 0; i < srcs.length; i++) srcs[i].setAttribute('src', srcs[i].getAttribute('data-bn-src'));
      v.load();
      return true;
    }
    if (!matches && on) {
      // Leaving the breakpoint: stop the download and give the memory back. The poster
      // still paints, so the hidden element looks the same as it ever did.
      for (i = 0; i < srcs.length; i++) srcs[i].removeAttribute('src');
      v.pause();
      v.removeAttribute('src');
      v.load();
      return false;
    }
    return matches;
  }

  /* ---- buffering ---------------------------------------------------------- */

  // Attach the source while the film is still below the fold, so the element has its
  // metadata by the time it is scrolled to and play() does not start from nothing.
  //
  // Deliberately NOT preload="auto". Lifting it here was measured and made things worse:
  // every film within 400px of the viewport downloaded in full whether or not the reader
  // ever stopped on it, and the page went from 108MB to 120MB. Metadata plus a play() when
  // the film is actually being looked at is what streams the least.
  function warm(v) {
    if (v.dataset.bnMediaWarm) return;
    v.dataset.bnMediaWarm = '1';
    applyQuery(v);
  }

  // play() rejects when the film has not buffered enough yet, which is ordinary on a slow
  // connection and on a page carrying a dozen films. Swallowing that rejection left the film
  // parked on its poster forever, because nothing ever asked again. Retry once the browser
  // says it can play, and give up quietly after that so a genuinely blocked film does not
  // spin.
  function attempt(v) {
    var btn = btnFor(v);
    warm(v);
    var p = v.play();
    if (!p || !p.catch) return;
    p.catch(function () {
      if (v.dataset.bnMediaRetried) return;
      v.dataset.bnMediaRetried = '1';
      v.addEventListener(
        'canplay',
        function () {
          if (held(v) || reduced() || v.dataset.bnMediaOffScreen) return;
          var again = v.play();
          if (again && again.catch) again.catch(function () {});
        },
        { once: true }
      );
    });
  }

  /* ---- init --------------------------------------------------------------- */

  function claimed(v) {
    // Leave the scrubbing and stage controllers their own elements.
    return v.closest('[data-bn-media]') || v.hasAttribute('data-bn-mq');
  }

  function init() {
    var vids = document.querySelectorAll('video[data-bn-mq], [data-bn-media] video');
    for (var i = 0; i < vids.length; i++) {
      (function (v) {
        if (!claimed(v) || v.dataset.bnMediaReady) return;
        v.dataset.bnMediaReady = '1';
        var btn = btnFor(v);

        applyQuery(v);
        var q = v.getAttribute('data-bn-mq');
        if (q) {
          var mq = window.matchMedia(q);
          var onChange = function () {
            applyQuery(v);
          };
          if (mq.addEventListener) mq.addEventListener('change', onChange);
          else if (mq.addListener) mq.addListener(onChange);
        }

        if (reduced()) {
          v.autoplay = false;
          v.pause();
          label(btn, false);
        }

        var media = v.closest ? v.closest('[data-bn-media]') : null;
        var automatic = !!(media && media.getAttribute('data-bn-media-autoplay') === 'true');

        // Keep the control honest if the browser starts or stops the film on its own.
        if (btn) {
          v.addEventListener('play', function () {
            label(btn, true);
          });
          v.addEventListener('pause', function () {
            label(btn, false);
          });
        }

        if (!automatic || !('IntersectionObserver' in window)) return;

        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) warm(v);
            });
          },
          { rootMargin: PREROLL }
        ).observe(v);

        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (held(v) || reduced()) return;
              var vh = window.innerHeight || document.documentElement.clientHeight || 1;
              var coverage = entry.intersectionRect.height / vh;
              if (entry.intersectionRatio >= PLAY_AT || coverage >= COVER_AT) {
                delete v.dataset.bnMediaOffScreen;
                if (applyQuery(v)) attempt(v);
              } else if (entry.intersectionRatio < DROP_AT && coverage < 0.1) {
                v.dataset.bnMediaOffScreen = '1';
                v.pause();
              }
            });
          },
          { threshold: [0, DROP_AT, PLAY_AT, COVER_AT] }
        ).observe(v);
      })(vids[i]);
    }
  }

  // A film left running in a background tab is battery for nothing. Resume only what the
  // page itself paused, never a film the reader stopped by hand.
  document.addEventListener('visibilitychange', function () {
    var vids = document.querySelectorAll('[data-bn-media] video');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (document.hidden) {
        if (!v.paused) {
          v.dataset.bnMediaTabPaused = '1';
          v.pause();
        }
      } else if (v.dataset.bnMediaTabPaused) {
        delete v.dataset.bnMediaTabPaused;
        if (!held(v) && !reduced() && !v.dataset.bnMediaOffScreen) attempt(v);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', init);
})();
